import type { FitProfile } from './fit';
import type { SeatCandidate } from './seat-identity';

export type CompanionType = 'solo' | 'adult' | 'child' | 'senior' | 'mobility';
export type RecoveryConcern = 'ticket' | 'seat_mismatch' | 'view' | 'late' | 'mobility' | 'sound' | 'neighbor' | 'exit';
export type VisitOutcome = 'better' | 'as_expected' | 'worse' | 'not_recorded';
export type EventType = 'performance' | 'movie';
export type SeatPriority = 'full_view' | 'close_view' | 'legroom' | 'aisle' | 'low_stairs' | 'lower_sensory';
export type SeatDealbreaker = 'separated' | 'many_stairs' | 'limited_view' | 'strong_sound' | 'middle_seat' | 'slow_exit';
export type TicketMethod = 'unknown' | 'mobile' | 'box_office' | 'delivery';
export type TimelineCheckpointKind = 'ticket_pickup' | 'merch' | 'doors' | 'seat_by' | 'intermission' | 'last_transport';
export type OutcomeDimension = 'view' | 'legroom' | 'sound' | 'access' | 'exit';

export interface CompanionPreference {
  id: string;
  label: string;
  priorities: SeatPriority[];
  dealbreakers: SeatDealbreaker[];
}

export interface TimelineCheckpoint {
  kind: TimelineCheckpointKind;
  at: string;
}

export interface VenueMemory {
  venueName: string;
  parkingNote: string;
  lockerNote: string;
  merchNote: string;
  exitNote: string;
  lastTransportNote: string;
}

export interface ViewingPlan {
  id?: string;
  candidateSnapshot?: SeatCandidate;
  memoryPhotoId: string | null;
  eventTitle: string;
  eventType: EventType;
  startsAt: string;
  eventDurationMinutes: number;
  travelMinutes: number | null;
  calendarReminderMinutes: number | null;
  ticketMethod: TicketMethod;
  companionType: CompanionType;
  headCount: number;
  budgetPerPerson: number | null;
  seatPriorities: SeatPriority[];
  companionPreferences: CompanionPreference[];
  connectedSeatsRequired: boolean;
  arrivalBufferMinutes: number;
  transportNote: string;
  officialInfoUrl: string;
  entranceNote: string;
  accessibilityNote: string;
  restroomNote: string;
  venueMemory: VenueMemory;
  timelineCheckpoints: TimelineCheckpoint[];
  completedItemIds: string[];
  recoveryConcern: RecoveryConcern | null;
  resolvedRecoveryConcerns: RecoveryConcern[];
  actualPaid: number | null;
  outcome: VisitOutcome;
  outcomeDimensions: Record<OutcomeDimension, VisitOutcome>;
  outcomeNote: string;
  nextSeatRule: string;
  updatedAt: string | null;
}

export interface ViewingHistoryEntry {
  id: string;
  memoryPhotoId: string | null;
  eventTitle: string;
  startsAt: string;
  companionType: CompanionType;
  seatPriorities: SeatPriority[];
  companionSummary: string;
  venueMemory: VenueMemory;
  actualPaid: number | null;
  outcome: VisitOutcome;
  outcomeDimensions: Record<OutcomeDimension, VisitOutcome>;
  outcomeNote: string;
  nextSeatRule: string;
  savedAt: string;
}

export interface JourneyContentItem {
  id: string;
  family: 'choice' | 'arrival' | 'recovery' | 'memory';
  title: string;
  description: string;
  appliesWhen: 'always' | CompanionType;
}

export const viewingJourneyContent: JourneyContentItem[] = [
  { id: 'SEAT-CHOICE-BUDGET', family: 'choice', title: '일행 전체 금액 확인', description: '후보 가격에 인원수를 곱하고 수수료·현장 차액은 모름으로 남겨요.', appliesWhen: 'always' },
  { id: 'SEAT-CHOICE-COMPANION', family: 'choice', title: '붙어 앉을 조건 확인', description: '같이 보는 사람과 통로·연석·눈높이 중 무엇이 중요한지 먼저 맞춰요.', appliesWhen: 'adult' },
  { id: 'SEAT-CHOICE-CHILD', family: 'choice', title: '아이 눈높이와 휴식 확인', description: '앞사람 가림과 중간 퇴장 가능성을 좌석 판정과 함께 봐요.', appliesWhen: 'child' },
  { id: 'SEAT-CHOICE-UNKNOWN', family: 'choice', title: '모름을 한 번 더 확인', description: '인증 기록이 없는 항목을 좋은 자리로 추측하지 않고 공식 예매처에서 다시 확인해요.', appliesWhen: 'always' },
  { id: 'SEAT-CHOICE-PRIORITY', family: 'choice', title: '이번 관람의 한 가지 우선순위', description: '가까움·전체 시야·무릎·통로·계단·감각 중 양보하기 어려운 조건을 먼저 골라요.', appliesWhen: 'always' },
  { id: 'SEAT-CHOICE-TOGETHER', family: 'choice', title: '붙어 앉아야 하는지 확인', description: '연석이 꼭 필요한지 정하고, 떨어진 좌석을 자동으로 괜찮다고 판단하지 않아요.', appliesWhen: 'adult' },
  { id: 'SEAT-CHOICE-SENIOR', family: 'choice', title: '계단과 쉬는 동선 확인', description: '부모님과 볼 때 계단 수·통로·중간 휴식 가능 위치를 공식 정보에서 확인해요.', appliesWhen: 'senior' },
  { id: 'SEAT-CHOICE-MOBILITY', family: 'choice', title: '이동 지원 좌석을 공식 확인', description: '접근 가능 여부는 좌석 사진으로 단정하지 않고 공연장 또는 영화관의 공식 안내에 확인해요.', appliesWhen: 'mobility' },
  { id: 'SEAT-ARRIVE-TICKET', family: 'arrival', title: '티켓 수령 방식을 확인해요', description: '모바일 티켓인지 현장 수령인지, 예매자 본인 확인이 필요한지 공식 안내를 열어 봐요.', appliesWhen: 'always' },
  { id: 'SEAT-ARRIVE-PROOF', family: 'arrival', title: '할인 증빙을 챙겨요', description: '할인을 썼다면 필요한 원본 증빙과 현장 차액 조건을 공식 안내에서 확인해요.', appliesWhen: 'always' },
  { id: 'SEAT-ARRIVE-ROUTE', family: 'arrival', title: '입구까지 걸리는 시간을 더해요', description: '주차나 역 도착 시간이 아니라 매표소·보안검색·객석 입구까지의 여유를 잡아요.', appliesWhen: 'always' },
  { id: 'SEAT-ARRIVE-LATE', family: 'arrival', title: '지연 입장 규칙을 확인해요', description: '공연마다 지연 입장 가능 시점과 대체 좌석 여부가 달라 공식 회차 안내를 확인해요.', appliesWhen: 'always' },
  { id: 'SEAT-ARRIVE-RESTROOM', family: 'arrival', title: '화장실과 객석 입구의 거리를 확인해요', description: '쉬는 시간에 다시 돌아올 시간을 예상할 수 있게 입구·화장실 동선을 메모해요.', appliesWhen: 'always' },
  { id: 'SEAT-ARRIVE-ACCESS', family: 'arrival', title: '엘리베이터·계단 경로를 확인해요', description: '공식 접근 안내가 없으면 가능하다고 추측하지 않고 현장 문의할 내용을 남겨요.', appliesWhen: 'always' },
  { id: 'SEAT-RECOVER-VIEW', family: 'recovery', title: '예상 밖 가림이 보여요', description: '촬영이나 임의 이동 대신 시작 전 안내원에게 현재 좌석에서 확인 가능한 조치를 물어봐요.', appliesWhen: 'always' },
  { id: 'SEAT-RECOVER-TICKET', family: 'recovery', title: '티켓 확인이 되지 않아요', description: '주문번호나 바코드를 공개하지 않고 예매처·본인 확인·재발급 창구 순서를 직원에게 확인해요.', appliesWhen: 'always' },
  { id: 'SEAT-RECOVER-SEAT', family: 'recovery', title: '티켓과 좌석 표기가 달라요', description: '다른 관객과 직접 다투지 않고 티켓 표기와 좌석 번호를 안내원에게 함께 보여 줘요.', appliesWhen: 'always' },
  { id: 'SEAT-RECOVER-MOBILITY', family: 'recovery', title: '이동 동선이 막혔어요', description: '현장 직원에게 접근 가능한 출입구·엘리베이터·동반석을 확인하고 보장되지 않은 경로는 따르지 않아요.', appliesWhen: 'mobility' },
  { id: 'SEAT-RECOVER-SOUND', family: 'recovery', title: '소리·빛이 너무 강해요', description: '안전하게 나갈 통로와 재입장 가능 여부를 직원에게 먼저 확인해요.', appliesWhen: 'always' },
  { id: 'SEAT-RECOVER-EXIT', family: 'recovery', title: '중간에 나가야 해요', description: '재입장과 인터미션은 회차마다 달라요. 이동 전에 안내원에게 가능한 시점을 확인해요.', appliesWhen: 'always' },
  { id: 'SEAT-RECOVER-LATE', family: 'recovery', title: '도착이 늦어졌어요', description: '객석으로 바로 뛰어가지 않고 지연 입장 위치와 가능한 시점을 안내원에게 확인해요.', appliesWhen: 'always' },
  { id: 'SEAT-RECOVER-NEIGHBOR', family: 'recovery', title: '옆자리 상황이 관람을 방해해요', description: '직접 충돌하거나 촬영하지 않고 안전한 이동·중재 가능 여부를 안내원에게 조용히 물어봐요.', appliesWhen: 'always' },
  { id: 'SEAT-MEMORY-OUTCOME', family: 'memory', title: '예상과 실제 차이를 남겨요', description: '좋고 나쁨만 고르지 않고 무엇이 달랐는지 한 문장으로 남겨요.', appliesWhen: 'always' },
  { id: 'SEAT-MEMORY-RULE', family: 'memory', title: '다음 좌석 규칙을 한 줄로 정해요', description: '다음 예매에서 바로 쓸 수 있게 다시 고를 조건이나 피할 조건을 한 줄로 남겨요.', appliesWhen: 'always' },
  { id: 'SEAT-MEMORY-PRICE', family: 'memory', title: '실제 낸 금액을 비교해요', description: '정가가 아니라 실제 1인 지출을 남겨 다음 좌석의 값어치를 비교해요.', appliesWhen: 'always' },
  { id: 'SEAT-MEMORY-COMPANION', family: 'memory', title: '같은 일행의 다음 조건을 남겨요', description: '아이·어르신·이동 지원이 필요한 일행과 다시 온다면 유지하거나 바꿀 조건을 적어요.', appliesWhen: 'always' },
];

export const initialViewingPlan: ViewingPlan = {
  memoryPhotoId: null,
  eventTitle: '',
  eventType: 'performance',
  startsAt: '',
  eventDurationMinutes: 150,
  travelMinutes: null,
  calendarReminderMinutes: 1_440,
  ticketMethod: 'unknown',
  companionType: 'solo',
  headCount: 1,
  budgetPerPerson: null,
  seatPriorities: [],
  companionPreferences: [],
  connectedSeatsRequired: true,
  arrivalBufferMinutes: 30,
  transportNote: '',
  officialInfoUrl: '',
  entranceNote: '',
  accessibilityNote: '',
  restroomNote: '',
  venueMemory: { venueName: '', parkingNote: '', lockerNote: '', merchNote: '', exitNote: '', lastTransportNote: '' },
  timelineCheckpoints: [],
  completedItemIds: [],
  recoveryConcern: null,
  resolvedRecoveryConcerns: [],
  actualPaid: null,
  outcome: 'not_recorded',
  outcomeDimensions: { view: 'not_recorded', legroom: 'not_recorded', sound: 'not_recorded', access: 'not_recorded', exit: 'not_recorded' },
  outcomeNote: '',
  nextSeatRule: '',
  updatedAt: null,
};

const companionTypes = new Set<CompanionType>(['solo', 'adult', 'child', 'senior', 'mobility']);
const eventTypes = new Set<EventType>(['performance', 'movie']);
const seatPriorities = new Set<SeatPriority>(['full_view', 'close_view', 'legroom', 'aisle', 'low_stairs', 'lower_sensory']);
const seatDealbreakers = new Set<SeatDealbreaker>(['separated', 'many_stairs', 'limited_view', 'strong_sound', 'middle_seat', 'slow_exit']);
const recoveryConcerns = new Set<RecoveryConcern>(['ticket', 'seat_mismatch', 'view', 'late', 'mobility', 'sound', 'neighbor', 'exit']);
const visitOutcomes = new Set<VisitOutcome>(['better', 'as_expected', 'worse', 'not_recorded']);
const ticketMethods = new Set<TicketMethod>(['unknown', 'mobile', 'box_office', 'delivery']);
const timelineKinds = new Set<TimelineCheckpointKind>(['ticket_pickup', 'merch', 'doors', 'seat_by', 'intermission', 'last_transport']);
const outcomeDimensions: OutcomeDimension[] = ['view', 'legroom', 'sound', 'access', 'exit'];

export function normalizeViewingPlan(value: unknown): ViewingPlan {
  if (!value || typeof value !== 'object') return { ...initialViewingPlan };
  const row = value as Partial<ViewingPlan>;
  const numberOrNull = (input: unknown) => typeof input === 'number' && Number.isFinite(input) ? input : null;
  return {
    ...initialViewingPlan,
    ...(typeof row.id === 'string' ? {id:row.id} : {}),
    ...(isSeatCandidate(row.candidateSnapshot) ? {candidateSnapshot:row.candidateSnapshot} : {}),
    memoryPhotoId: typeof row.memoryPhotoId === 'string' && /^[a-zA-Z0-9_-]{1,120}$/.test(row.memoryPhotoId) ? row.memoryPhotoId : null,
    eventTitle: typeof row.eventTitle === 'string' ? row.eventTitle.slice(0, 120) : '',
    eventType: eventTypes.has(row.eventType as EventType) ? row.eventType as EventType : 'performance',
    startsAt: typeof row.startsAt === 'string' ? row.startsAt : '',
    eventDurationMinutes: typeof row.eventDurationMinutes === 'number' ? row.eventDurationMinutes : 150,
    travelMinutes: numberOrNull(row.travelMinutes),
    calendarReminderMinutes: numberOrNull(row.calendarReminderMinutes),
    ticketMethod: ticketMethods.has(row.ticketMethod as TicketMethod) ? row.ticketMethod as TicketMethod : 'unknown',
    companionType: companionTypes.has(row.companionType as CompanionType) ? row.companionType as CompanionType : 'solo',
    headCount: typeof row.headCount === 'number' ? row.headCount : 1,
    budgetPerPerson: numberOrNull(row.budgetPerPerson),
    seatPriorities: Array.isArray(row.seatPriorities) ? row.seatPriorities.filter((item): item is SeatPriority => seatPriorities.has(item as SeatPriority)).slice(0, 3) : [],
    companionPreferences: normalizeCompanionPreferences(row.companionPreferences),
    connectedSeatsRequired: typeof row.connectedSeatsRequired === 'boolean' ? row.connectedSeatsRequired : true,
    arrivalBufferMinutes: typeof row.arrivalBufferMinutes === 'number' ? row.arrivalBufferMinutes : 30,
    transportNote: typeof row.transportNote === 'string' ? row.transportNote.slice(0, 240) : '',
    officialInfoUrl: typeof row.officialInfoUrl === 'string' ? row.officialInfoUrl.slice(0, 500) : '',
    entranceNote: typeof row.entranceNote === 'string' ? row.entranceNote.slice(0, 240) : '',
    accessibilityNote: typeof row.accessibilityNote === 'string' ? row.accessibilityNote.slice(0, 240) : '',
    restroomNote: typeof row.restroomNote === 'string' ? row.restroomNote.slice(0, 240) : '',
    venueMemory: normalizeVenueMemory(row.venueMemory),
    timelineCheckpoints: normalizeTimelineCheckpoints(row.timelineCheckpoints),
    completedItemIds: Array.isArray(row.completedItemIds) ? row.completedItemIds.filter((item): item is string => typeof item === 'string' && viewingJourneyContent.some((content) => content.id === item)) : [],
    recoveryConcern: recoveryConcerns.has(row.recoveryConcern as RecoveryConcern) ? row.recoveryConcern as RecoveryConcern : null,
    resolvedRecoveryConcerns: Array.isArray(row.resolvedRecoveryConcerns) ? [...new Set(row.resolvedRecoveryConcerns.filter((item): item is RecoveryConcern => recoveryConcerns.has(item as RecoveryConcern)))].slice(0, recoveryConcerns.size) : [],
    actualPaid: numberOrNull(row.actualPaid),
    outcome: visitOutcomes.has(row.outcome as VisitOutcome) ? row.outcome as VisitOutcome : 'not_recorded',
    outcomeDimensions: normalizeOutcomeDimensions(row.outcomeDimensions),
    outcomeNote: typeof row.outcomeNote === 'string' ? row.outcomeNote.slice(0, 500) : '',
    nextSeatRule: typeof row.nextSeatRule === 'string' ? row.nextSeatRule.slice(0, 240) : '',
    updatedAt: typeof row.updatedAt === 'string' ? row.updatedAt : null,
  };
}

export function visibleJourneyItems(plan: ViewingPlan): JourneyContentItem[] {
  return viewingJourneyContent.filter((item) => item.appliesWhen === 'always' || item.appliesWhen === plan.companionType);
}

export function estimateGroupCost(plan: ViewingPlan, candidates: SeatCandidate[]): { known: number | null; unknown: boolean } {
  const candidatePrices = candidates.map((candidate) => candidate.expectedPrice).filter((price): price is number => price !== null);
  const perPerson = plan.budgetPerPerson ?? (candidatePrices.length ? Math.max(...candidatePrices) : null);
  return { known: perPerson === null ? null : perPerson * Math.max(1, plan.headCount), unknown: perPerson === null || candidatePrices.length !== candidates.length };
}

export function buildViewingCalendar(plan: ViewingPlan): string {
  if (!plan.startsAt) throw new Error('관람 시작 시간을 먼저 입력해 주세요.');
  const start = new Date(plan.startsAt);
  if (Number.isNaN(start.getTime())) throw new Error('관람 시작 시간을 확인해 주세요.');
  const end = new Date(start.getTime() + plan.eventDurationMinutes * 60_000);
  const stamp = (date: Date) => date.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}Z$/, 'Z');
  const escape = (value: string) => value.replace(/\\/g, '\\\\').replace(/,/g, '\\,').replace(/;/g, '\\;').replace(/\n/g, '\\n');
  return [
    'BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//Anjabom//Viewing Plan//KO', 'BEGIN:VEVENT',
    `UID:anjabom-${start.getTime()}@local`, `DTSTAMP:${stamp(new Date())}`, `DTSTART:${stamp(start)}`, `DTEND:${stamp(end)}`,
    `SUMMARY:${escape(plan.eventTitle || '앉아봄 관람')}`,
    ...(plan.venueMemory.venueName ? [`LOCATION:${escape(plan.venueMemory.venueName)}`] : []),
    `DESCRIPTION:${escape([`도착 여유 ${plan.arrivalBufferMinutes}분`, plan.transportNote, plan.entranceNote].filter(Boolean).join(' · ') || '앉아봄 관람 준비')}`,
    ...(plan.calendarReminderMinutes === null ? [] : ['BEGIN:VALARM', `TRIGGER:-PT${plan.calendarReminderMinutes}M`, 'ACTION:DISPLAY', `DESCRIPTION:${escape(`${plan.eventTitle || '관람'} 준비를 확인해요.`)}`, 'END:VALARM']),
    'END:VEVENT', 'END:VCALENDAR', '',
  ].join('\r\n');
}

export function exportViewingPlan(plan: ViewingPlan, candidates: SeatCandidate[]): string {
  return JSON.stringify({
    schemaVersion: 5,
    exportedAt: new Date().toISOString(),
    photoIncluded: false,
    plan: { ...normalizeViewingPlan(plan), memoryPhotoId: null },
    candidates,
  }, null, 2);
}

export function importViewingPlan(text: string): { plan: ViewingPlan; candidates: SeatCandidate[] } {
  const parsed = JSON.parse(text) as { schemaVersion?: unknown; plan?: unknown; candidates?: unknown };
  if (![1, 2, 3, 4, 5].includes(Number(parsed.schemaVersion))) throw new Error('앉아봄 관람 준비 백업 파일인지 확인해 주세요.');
  const plan = normalizeViewingPlan(parsed.plan);
  const errors = validateViewingPlan(plan);
  if (errors.length) throw new Error(errors[0]);
  const candidates = Array.isArray(parsed.candidates) ? parsed.candidates.filter(isSeatCandidate).slice(0, 4) : [];
  return { plan, candidates };
}

function isSeatCandidate(value: unknown): value is SeatCandidate {
  if (!value || typeof value !== 'object') return false;
  const row = value as Partial<SeatCandidate>;
  return (row.categoryId === 'performance' || row.categoryId === 'cinema')
    && ['candidateId', 'venueId', 'venueName', 'spaceId', 'spaceName', 'layoutVersionId', 'sectionCode', 'rowLabel'].every((key) => typeof row[key as keyof SeatCandidate] === 'string')
    && Number.isInteger(row.seatNumber) && Number(row.seatNumber) > 0
    && (row.expectedPrice === null || (typeof row.expectedPrice === 'number' && Number.isFinite(row.expectedPrice) && row.expectedPrice >= 0));
}

export function validateViewingPlan(plan: ViewingPlan): string[] {
  const errors: string[] = [];
  if (plan.headCount < 1 || plan.headCount > 12) errors.push('인원은 1명부터 12명까지 입력해 주세요.');
  if (plan.budgetPerPerson !== null && (plan.budgetPerPerson < 0 || plan.budgetPerPerson > 10_000_000)) errors.push('1인 예산을 확인해 주세요.');
  if (plan.arrivalBufferMinutes < 0 || plan.arrivalBufferMinutes > 240) errors.push('도착 여유는 0분부터 240분까지 입력해 주세요.');
  if (plan.eventDurationMinutes < 15 || plan.eventDurationMinutes > 720) errors.push('관람 시간은 15분부터 12시간까지 입력해 주세요.');
  if (plan.travelMinutes !== null && (plan.travelMinutes < 0 || plan.travelMinutes > 720)) errors.push('이동 시간은 0분부터 12시간까지 입력해 주세요.');
  if (plan.calendarReminderMinutes !== null && (plan.calendarReminderMinutes < 0 || plan.calendarReminderMinutes > 10_080)) errors.push('캘린더 알림은 일주일 전까지 설정할 수 있어요.');
  if (plan.seatPriorities.length > 3) errors.push('좌석 우선순위는 3개까지 골라 주세요.');
  if (plan.companionPreferences.length > 6) errors.push('동행 조건은 여섯 자리까지 정리할 수 있어요.');
  if (plan.actualPaid !== null && (plan.actualPaid < 0 || plan.actualPaid > 10_000_000)) errors.push('실제 지출을 확인해 주세요.');
  if (plan.officialInfoUrl && !/^https:\/\//i.test(plan.officialInfoUrl)) errors.push('공식 안내 주소는 https://로 시작해야 해요.');
  return errors;
}

function normalizeCompanionPreferences(value: unknown): CompanionPreference[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((item, index) => {
    if (!item || typeof item !== 'object') return [];
    const row = item as Partial<CompanionPreference>;
    return [{
      id: typeof row.id === 'string' && row.id ? row.id.slice(0, 48) : `companion-${index + 1}`,
      label: typeof row.label === 'string' && row.label.trim() ? row.label.trim().slice(0, 20) : index === 0 ? '나' : `일행 ${index}`,
      priorities: Array.isArray(row.priorities) ? [...new Set(row.priorities.filter((entry): entry is SeatPriority => seatPriorities.has(entry as SeatPriority)))].slice(0, 3) : [],
      dealbreakers: Array.isArray(row.dealbreakers) ? [...new Set(row.dealbreakers.filter((entry): entry is SeatDealbreaker => seatDealbreakers.has(entry as SeatDealbreaker)))].slice(0, 3) : [],
    }];
  }).slice(0, 6);
}

function normalizeVenueMemory(value: unknown): VenueMemory {
  const row = value && typeof value === 'object' ? value as Partial<VenueMemory> : {};
  const text = (input: unknown, max = 240) => typeof input === 'string' ? input.slice(0, max) : '';
  return { venueName: text(row.venueName, 120), parkingNote: text(row.parkingNote), lockerNote: text(row.lockerNote), merchNote: text(row.merchNote), exitNote: text(row.exitNote), lastTransportNote: text(row.lastTransportNote) };
}

function normalizeTimelineCheckpoints(value: unknown): TimelineCheckpoint[] {
  if (!Array.isArray(value)) return [];
  const byKind = new Map<TimelineCheckpointKind, TimelineCheckpoint>();
  for (const item of value) {
    if (!item || typeof item !== 'object') continue;
    const row = item as Partial<TimelineCheckpoint>;
    if (!timelineKinds.has(row.kind as TimelineCheckpointKind) || typeof row.at !== 'string' || !row.at) continue;
    if (Number.isNaN(new Date(row.at).getTime())) continue;
    byKind.set(row.kind as TimelineCheckpointKind, { kind: row.kind as TimelineCheckpointKind, at: row.at });
  }
  return [...byKind.values()];
}

function normalizeOutcomeDimensions(value: unknown): Record<OutcomeDimension, VisitOutcome> {
  const row = value && typeof value === 'object' ? value as Partial<Record<OutcomeDimension, VisitOutcome>> : {};
  return Object.fromEntries(outcomeDimensions.map((key) => [key, visitOutcomes.has(row[key] as VisitOutcome) ? row[key] : 'not_recorded'])) as Record<OutcomeDimension, VisitOutcome>;
}

export interface ViewingDayBrief {
  startsAt: Date | null;
  arrivalTargetAt: Date | null;
  leaveTargetAt: Date | null;
  phase: 'date_needed' | 'upcoming' | 'soon' | 'in_progress_or_finished';
  remainingChecks: number;
}

export interface ViewingTimelineRow {
  kind: TimelineCheckpointKind | 'leave' | 'arrival' | 'start' | 'expected_end';
  label: string;
  at: Date;
  source: 'user_entered' | 'derived_from_user_input';
}

export interface CompanionAgreement {
  participantCount: number;
  sharedPriorities: SeatPriority[];
  differentPriorities: SeatPriority[];
  dealbreakers: SeatDealbreaker[];
  missingParticipants: number;
  summary: string;
}

export interface RecoveryGuide {
  title: string;
  firstAction: string;
  staffMessage: string;
  doNot: string;
}

export function buildViewingDayBrief(plan: ViewingPlan, remainingChecks: number, now = new Date()): ViewingDayBrief {
  if (!plan.startsAt) return { startsAt: null, arrivalTargetAt: null, leaveTargetAt: null, phase: 'date_needed', remainingChecks };
  const startsAt = new Date(plan.startsAt);
  if (Number.isNaN(startsAt.getTime())) return { startsAt: null, arrivalTargetAt: null, leaveTargetAt: null, phase: 'date_needed', remainingChecks };
  const arrivalTargetAt = new Date(startsAt.getTime() - plan.arrivalBufferMinutes * 60_000);
  const leaveTargetAt = plan.travelMinutes === null ? null : new Date(arrivalTargetAt.getTime() - plan.travelMinutes * 60_000);
  const hours = (startsAt.getTime() - now.getTime()) / 3_600_000;
  const phase = hours < 0 ? 'in_progress_or_finished' : hours <= 24 ? 'soon' : 'upcoming';
  return { startsAt, arrivalTargetAt, leaveTargetAt, phase, remainingChecks };
}

const timelineLabels: Record<TimelineCheckpointKind, string> = {
  ticket_pickup: '티켓 수령·본인 확인', merch: '굿즈·매점', doors: '입장 시작', seat_by: '좌석에 앉은 시각', intermission: '인터미션', last_transport: '막차',
};

export function buildViewingTimeline(plan: ViewingPlan): { rows: ViewingTimelineRow[]; conflicts: string[] } {
  const rows: ViewingTimelineRow[] = [];
  const conflicts: string[] = [];
  const start = plan.startsAt ? new Date(plan.startsAt) : null;
  const validStart = start && !Number.isNaN(start.getTime()) ? start : null;
  if (validStart) {
    const arrival = new Date(validStart.getTime() - plan.arrivalBufferMinutes * 60_000);
    if (plan.travelMinutes !== null) rows.push({ kind: 'leave', label: '출발 목표', at: new Date(arrival.getTime() - plan.travelMinutes * 60_000), source: 'derived_from_user_input' });
    rows.push({ kind: 'arrival', label: '도착 목표', at: arrival, source: 'derived_from_user_input' });
  }
  for (const checkpoint of plan.timelineCheckpoints) {
    if (checkpoint.kind === 'seat_by') continue;
    const at = new Date(checkpoint.at);
    if (!Number.isNaN(at.getTime())) rows.push({ kind: checkpoint.kind, label: timelineLabels[checkpoint.kind], at, source: 'user_entered' });
  }
  if (validStart) {
    rows.push({ kind: 'start', label: '관람 시작', at: validStart, source: 'user_entered' });
    rows.push({ kind: 'expected_end', label: '예상 종료', at: new Date(validStart.getTime() + plan.eventDurationMinutes * 60_000), source: 'derived_from_user_input' });
  }
  rows.sort((a, b) => a.at.getTime() - b.at.getTime());
  const find = (kind: ViewingTimelineRow['kind']) => rows.find((row) => row.kind === kind)?.at.getTime() ?? null;
  const startMs = find('start');
  const endMs = find('expected_end');
  const ticketMs = find('ticket_pickup');
  const intermissionMs = find('intermission');
  const lastTransportMs = find('last_transport');
  if (startMs !== null && ticketMs !== null && ticketMs >= startMs) conflicts.push('티켓 수령 목표가 관람 시작보다 늦어요. 시간을 다시 확인해 주세요.');
  if (startMs !== null && endMs !== null && intermissionMs !== null && (intermissionMs <= startMs || intermissionMs >= endMs)) conflicts.push('인터미션은 관람 시작과 예상 종료 사이로 입력해 주세요.');
  if (endMs !== null && lastTransportMs !== null && lastTransportMs <= endMs) conflicts.push('막차가 예상 종료보다 빨라요. 중간 퇴장 또는 다른 이동 방법을 먼저 정해 주세요.');
  return { rows, conflicts };
}

export function buildCompanionAgreement(plan: ViewingPlan): CompanionAgreement {
  const participants = plan.companionPreferences.slice(0, Math.min(plan.headCount, 6));
  const priorityCounts = new Map<SeatPriority, number>();
  for (const participant of participants) for (const priority of participant.priorities) priorityCounts.set(priority, (priorityCounts.get(priority) ?? 0) + 1);
  const sharedPriorities = participants.length > 1 ? [...priorityCounts].filter(([, count]) => count === participants.length).map(([priority]) => priority) : participants[0]?.priorities ?? plan.seatPriorities;
  const differentPriorities = [...priorityCounts].filter(([, count]) => count < participants.length).sort((a, b) => b[1] - a[1]).map(([priority]) => priority);
  const dealbreakers = [...new Set(participants.flatMap((participant) => participant.dealbreakers))];
  const missingParticipants = Math.max(0, Math.min(plan.headCount, 6) - participants.length);
  const summary = !participants.length
    ? '일행별 조건을 넣으면 공통 조건과 꼭 피할 조건을 분리해요.'
    : sharedPriorities.length
      ? `함께 지킬 조건 ${sharedPriorities.length}개와 꼭 피할 조건 ${dealbreakers.length}개를 먼저 봐요.`
      : `공통 우선순위가 아직 없어요. 서로 다른 조건 ${differentPriorities.length}개를 예매 전에 맞춰요.`;
  return { participantCount: participants.length, sharedPriorities, differentPriorities, dealbreakers, missingParticipants, summary };
}

const recoveryGuides: Record<RecoveryConcern, Omit<RecoveryGuide, 'staffMessage'>> = {
  ticket: { title: '티켓 확인이 되지 않아요', firstAction: '주문번호·바코드는 주변에 보이지 않게 두고 공식 예매 내역을 열어요.', doNot: '바코드나 주문번호를 공개 채널에 올리지 않아요.' },
  seat_mismatch: { title: '티켓과 좌석 표기가 달라요', firstAction: '좌석을 점유한 사람과 직접 다투지 말고 티켓 표기와 좌석 번호를 함께 확인해요.', doNot: '상대방을 촬영하거나 임의로 다른 좌석에 앉지 않아요.' },
  view: { title: '예상 밖 가림이 보여요', firstAction: '관람 시작 전 현재 좌석에서 가능한 공식 조치가 있는지 안내원에게 물어봐요.', doNot: '촬영하거나 빈 좌석으로 임의 이동하지 않아요.' },
  late: { title: '도착이 늦어졌어요', firstAction: '객석으로 바로 뛰어가지 말고 지연 입장 위치와 가능한 시점을 확인해요.', doNot: '공연 중 문을 직접 열거나 다른 입구로 우회하지 않아요.' },
  mobility: { title: '이동 동선이 막혔어요', firstAction: '접근 가능한 출입구·엘리베이터·동반석을 현장 직원에게 확인해요.', doNot: '보장되지 않은 경사로나 직원 전용 동선을 사용하지 않아요.' },
  sound: { title: '소리·빛이 너무 강해요', firstAction: '안전하게 나갈 통로와 재입장 가능 여부를 먼저 확인해요.', doNot: '어두운 객석에서 급하게 이동하지 않아요.' },
  neighbor: { title: '옆자리 상황이 관람을 방해해요', firstAction: '안내원에게 조용히 상황을 말하고 중재 또는 가능한 이동을 물어봐요.', doNot: '직접 충돌하거나 상대방을 촬영하지 않아요.' },
  exit: { title: '중간에 나가야 해요', firstAction: '이동 전에 재입장 가능 시점과 가장 가까운 안전 출구를 확인해요.', doNot: '인터미션이나 재입장이 있다고 추측하지 않아요.' },
};

export function buildRecoveryGuide(concern: RecoveryConcern, seatLabel = ''): RecoveryGuide {
  const guide = recoveryGuides[concern];
  const where = seatLabel ? `현재 ${seatLabel}에 앉아 있어요. ` : '';
  return { ...guide, staffMessage: `${where}${guide.title}. 이 회차에서 가능한 확인 순서와 안전한 조치를 알려 주세요.` };
}

const seatPriorityLabels: Record<SeatPriority, string> = {
  full_view: '무대·화면 전체', close_view: '가까운 거리', legroom: '무릎 공간', aisle: '통로', low_stairs: '적은 계단', lower_sensory: '덜 강한 소리·빛',
};

export function buildSeatDna(plan: ViewingPlan, history: ViewingHistoryEntry[]): { tags: string[]; summary: string; evidenceCount: number } {
  const recent = history.slice(0, 5);
  const priorities = [...plan.seatPriorities, ...recent.flatMap((entry) => entry.seatPriorities)];
  const counts = priorities.reduce((acc, priority) => acc.set(priority, (acc.get(priority) ?? 0) + 1), new Map<SeatPriority, number>());
  const tags = [...counts.entries()].sort((a, b) => b[1] - a[1]).slice(0, 3).map(([priority]) => seatPriorityLabels[priority]);
  if (plan.connectedSeatsRequired && plan.headCount > 1) tags.push('붙어 앉기');
  const unique = [...new Set(tags)].slice(0, 4);
  return {
    tags: unique,
    summary: unique.length ? `${unique.join(' · ')} 조건을 다음 후보에서 먼저 확인해요.` : '이번 관람의 우선순위를 고르면 다음 좌석 기준으로 남아요.',
    evidenceCount: recent.filter((entry) => entry.outcome !== 'not_recorded').length,
  };
}

export function createViewingHistoryEntry(plan: ViewingPlan, savedAt = new Date().toISOString()): ViewingHistoryEntry {
  const normalized = normalizeViewingPlan(plan);
  const agreement = buildCompanionAgreement(normalized);
  return {
    id: `${Date.parse(savedAt) || Date.now()}-${normalized.eventTitle || 'viewing'}`,
    memoryPhotoId: normalized.memoryPhotoId,
    eventTitle: normalized.eventTitle || '이름 없는 관람',
    startsAt: normalized.startsAt,
    companionType: normalized.companionType,
    seatPriorities: normalized.seatPriorities,
    companionSummary: agreement.summary,
    venueMemory: normalized.venueMemory,
    actualPaid: normalized.actualPaid,
    outcome: normalized.outcome,
    outcomeDimensions: normalized.outcomeDimensions,
    outcomeNote: normalized.outcomeNote,
    nextSeatRule: normalized.nextSeatRule,
    savedAt,
  };
}

export function normalizeViewingHistoryEntry(value: unknown): ViewingHistoryEntry | null {
  if (!value || typeof value !== 'object') return null;
  const row = value as Partial<ViewingHistoryEntry>;
  if (typeof row.id !== 'string' || typeof row.eventTitle !== 'string' || typeof row.savedAt !== 'string') return null;
  return {
    id: row.id.slice(0, 180),
    memoryPhotoId: typeof row.memoryPhotoId === 'string' && /^[a-zA-Z0-9_-]{1,120}$/.test(row.memoryPhotoId) ? row.memoryPhotoId : null,
    eventTitle: row.eventTitle.slice(0, 120) || '이름 없는 관람',
    startsAt: typeof row.startsAt === 'string' ? row.startsAt : '',
    companionType: companionTypes.has(row.companionType as CompanionType) ? row.companionType as CompanionType : 'solo',
    seatPriorities: Array.isArray(row.seatPriorities) ? row.seatPriorities.filter((item): item is SeatPriority => seatPriorities.has(item as SeatPriority)).slice(0, 3) : [],
    companionSummary: typeof row.companionSummary === 'string' ? row.companionSummary.slice(0, 240) : '일행별 조건은 다음 관람에서 다시 확인해요.',
    venueMemory: normalizeVenueMemory(row.venueMemory),
    actualPaid: typeof row.actualPaid === 'number' && Number.isFinite(row.actualPaid) ? row.actualPaid : null,
    outcome: visitOutcomes.has(row.outcome as VisitOutcome) ? row.outcome as VisitOutcome : 'not_recorded',
    outcomeDimensions: normalizeOutcomeDimensions(row.outcomeDimensions),
    outcomeNote: typeof row.outcomeNote === 'string' ? row.outcomeNote.slice(0, 500) : '',
    nextSeatRule: typeof row.nextSeatRule === 'string' ? row.nextSeatRule.slice(0, 240) : '',
    savedAt: row.savedAt,
  };
}

export function fitReminder(profile: FitProfile): string {
  const needs = [profile.knee !== null && profile.knee >= 2 ? '무릎 공간' : null, profile.neckBack !== null && profile.neckBack >= 2 ? '목·허리 각도' : null, profile.soundLight !== null && profile.soundLight >= 2 ? '소리·빛' : null, profile.mobilityNeed ? '이동 동선' : null].filter(Boolean);
  return needs.length ? `${needs.join(' · ')} 조건을 후보와 다시 대조해요.` : '내 조건을 입력하면 관람 준비에 함께 반영해요.';
}
